import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  BookOpen, TrendingUp, TrendingDown, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock, Eye,
  BarChart3, Activity, Target, Brain
} from 'lucide-react';

// FAQ Data for Schema
const faqData = [
  {
    question: "RSI kaç olmalı?",
    answer: "RSI 30'un altına düştüğünde aşırı satım (oversold), 70'in üstüne çıktığında aşırı alım (overbought) bölgesi olarak kabul edilir. Ancak bu seviyeler mutlak kural değildir ve diğer göstergelerle birlikte değerlendirilmelidir."
  },
  {
    question: "RSI divergence ne demek?",
    answer: "RSI divergence, fiyat ile RSI'ın zıt yönde hareket etmesidir. Fiyat yeni dip yaparken RSI daha yüksek dip yaparsa bullish divergence, fiyat yeni zirve yaparken RSI daha düşük zirve yaparsa bearish divergence oluşur."
  },
  {
    question: "RSI hangi zaman diliminde kullanılmalı?",
    answer: "RSI her zaman diliminde kullanılabilir. Kısa vadeli trader'lar 5-15 dakikalık grafikleri, swing trader'lar 4 saatlik ve günlük grafikleri tercih eder. Uzun vadeli yatırımcılar haftalık grafiklerde RSI'ı değerlendirir."
  },
  {
    question: "RSI tek başına yeterli mi?",
    answer: "Hayır. RSI tek başına kullanılmamalıdır. MACD, hareketli ortalamalar, destek-direnç seviyeleri ve hacim gibi diğer göstergelerle birlikte değerlendirilmelidir."
  },
  {
    question: "RSI ile nasıl trade yapılır?",
    answer: "RSI ile trade yaparken: 1) Aşırı satım bölgesinde (30 altı) alım fırsatı aranır, 2) Aşırı alım bölgesinde (70 üstü) satış düşünülür, 3) Divergence'lar trend dönüşü sinyali verebilir, 4) 50 seviyesi trend yönü için referans olabilir."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "RSI Nedir? Relative Strength Index Kullanımı ve Yorumlanması",
    "description": "RSI (Relative Strength Index) nedir, nasıl hesaplanır ve nasıl yorumlanır? Kripto trading'de RSI kullanımı rehberi.",
    "author": { "@type": "Organization", "name": "Borsamex" },
    "publisher": {
      "@type": "Organization",
      "name": "Borsamex",
      "logo": { "@type": "ImageObject", "url": "https://borsamex.com/logo512.png" }
    },
    "datePublished": "2026-04-02",
    "dateModified": "2026-04-02",
    "mainEntityOfPage": "https://borsamex.com/rehber/rsi-nedir"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const RSINedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="RSI Nedir? Relative Strength Index Kullanımı ve Yorumlanması | Borsamex"
        description="RSI (Relative Strength Index) nedir, nasıl hesaplanır ve nasıl yorumlanır? Kripto trading'de RSI kullanımı, aşırı alım-satım bölgeleri ve divergence stratejileri."
        keywords="rsi nedir, relative strength index, rsi göstergesi, rsi nasıl yorumlanır, rsi divergence, aşırı alım aşırı satım, kripto teknik analiz, rsi stratejisi"
        canonical="https://borsamex.com/rehber/rsi-nedir"
        structuredData={structuredData}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">RSI Nedir?</span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Header */}
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">Teknik Analiz</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>8 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              RSI Nedir? (Relative Strength Index)
            </h1>
            
            {/* Featured Snippet Target */}
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-white">RSI (Relative Strength Index)</strong>, bir varlığın fiyat hareketlerinin 
                hızını ve değişimini ölçen momentum göstergesidir. 0-100 arasında bir değer üretir. 
                <strong className="text-brand-green"> 70 üzeri aşırı alım</strong>, 
                <strong className="text-red-400"> 30 altı aşırı satım</strong> bölgesi olarak kabul edilir.
              </p>
            </div>
          </motion.header>

          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="prose prose-invert prose-slate max-w-none"
          >
            
            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Activity className="h-4 w-4 text-blue-400" />
                </div>
                RSI Göstergesi Ne İşe Yarar?
              </h2>
              
              <p className="text-slate-300 mb-4">
                RSI (Göreceli Güç Endeksi), 1978 yılında J. Welles Wilder tarafından geliştirilen ve 
                dünya genelinde en yaygın kullanılan teknik analiz göstergelerinden biridir. Kripto, hisse 
                senedi ve forex piyasalarında aynı mantıkla kullanılır.
              </p>
              
              <p className="text-slate-300 mb-6">
                RSI'ın temel amacı, bir varlığın <strong className="text-white">aşırı alınıp alınmadığını</strong> veya 
                <strong className="text-white"> aşırı satılıp satılmadığını</strong> belirlemektir. Bu bilgi, potansiyel 
                dönüş noktalarını tespit etmek için kullanılır.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Card className="border-green-500/30 bg-green-950/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingDown className="h-5 w-5 text-green-400" />
                      <span className="font-bold text-green-400">RSI &lt; 30</span>
                    </div>
                    <p className="text-slate-300 text-sm">
                      <strong>Aşırı Satım Bölgesi:</strong> Fiyat çok düşmüş olabilir, alım fırsatı aranabilir.
                    </p>
                  </CardContent>
                </Card>
                
                <Card className="border-red-500/30 bg-red-950/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-red-400" />
                      <span className="font-bold text-red-400">RSI &gt; 70</span>
                    </div>
                    <p className="text-slate-300 text-sm">
                      <strong>Aşırı Alım Bölgesi:</strong> Fiyat çok yükselmiş olabilir, satış düşünülebilir.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Section 2 - Calculation */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <BarChart3 className="h-4 w-4 text-purple-400" />
                </div>
                RSI Nasıl Hesaplanır?
              </h2>
              
              <p className="text-slate-300 mb-4">
                RSI hesaplaması için genellikle son 14 periyot kullanılır (14 mum). Formül şu şekildedir:
              </p>
              
              <Card className="border-slate-700 bg-slate-900/50 mb-6">
                <CardContent className="p-6">
                  <code className="text-brand-green text-lg">
                    RSI = 100 - (100 / (1 + RS))
                  </code>
                  <p className="text-slate-400 mt-4 text-sm">
                    RS (Relative Strength) = Ortalama Kazanç / Ortalama Kayıp
                  </p>
                </CardContent>
              </Card>
              
              <p className="text-slate-300 mb-4">
                Neyse ki bu hesaplamayı kendiniz yapmanıza gerek yok. Tüm trading platformları ve 
                analiz araçları RSI'ı otomatik olarak hesaplar ve görselleştirir.
              </p>
            </section>

            {/* Inline Signal Box - 40% scroll point */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Örneği</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">ETH-USDT</span>
                      <Badge className="bg-green-500/20 text-green-400">LONG</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      RSI: 28 (Aşırı satım bölgesi) + Destek seviyesi testi
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>Sniper Engine tarafından tespit edildi</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Borsamex'in AI sistemi, RSI dahil 200+ göstergeyi analiz ederek sinyal üretir.
                  </p>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Tüm Sinyalleri İncele <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* Section 3 - How to Use */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-green-400" />
                </div>
                RSI Nasıl Yorumlanır?
              </h2>
              
              <p className="text-slate-300 mb-6">
                RSI yorumlarken dikkat edilmesi gereken temel kurallar:
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-brand-green font-bold shrink-0">1</div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Aşırı Alım/Satım Bölgeleri</h3>
                    <p className="text-slate-400 text-sm">
                      RSI 70'in üzerindeyse aşırı alım, 30'un altındaysa aşırı satım bölgesindedir. 
                      Ancak güçlü trendlerde fiyat uzun süre bu bölgelerde kalabilir.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-brand-green font-bold shrink-0">2</div>
                  <div>
                    <h3 className="text-white font-bold mb-1">50 Seviyesi</h3>
                    <p className="text-slate-400 text-sm">
                      RSI 50'nin üzerindeyse yükseliş trendi, altındaysa düşüş trendi hakimdir. 
                      50 seviyesinin geçilmesi trend değişikliği sinyali olabilir.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-brand-green font-bold shrink-0">3</div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Divergence (Uyumsuzluk)</h3>
                    <p className="text-slate-400 text-sm">
                      Fiyat yeni dip yaparken RSI daha yüksek dip yaparsa bullish divergence, 
                      fiyat yeni zirve yaparken RSI daha düşük zirve yaparsa bearish divergence oluşur.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-brand-green font-bold shrink-0">4</div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Destek-Direnç ile Birlikte</h3>
                    <p className="text-slate-400 text-sm">
                      RSI sinyalleri, <Link to="/rehber/destek-direnc-nedir" className="text-brand-green hover:underline">destek ve direnç seviyeleri</Link> ile 
                      birlikte değerlendirildiğinde daha güvenilir sonuçlar verir.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 4 - RSI in AI Trading */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Brain className="h-4 w-4 text-brand-green" />
                </div>
                AI Trading'de RSI Kullanımı
              </h2>
              
              <p className="text-slate-300 mb-4">
                Modern AI trading sistemleri, RSI gibi klasik göstergeleri çok daha gelişmiş şekilde kullanır. 
                Borsamex'in Sniper Engine'i RSI'ı şu şekilde değerlendirir:
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Çoklu zaman dilimi analizi:</strong> 5dk, 15dk, 1s, 4s RSI değerleri birlikte değerlendirilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Diğer göstergelerle korelasyon:</strong> <Link to="/rehber/macd-nedir" className="text-brand-green hover:underline">MACD</Link>, hacim, volatilite ile birlikte analiz</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Otomatik divergence tespiti:</strong> Bullish ve bearish divergence'lar otomatik tespit edilir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span><strong className="text-white">Dinamik seviyeler:</strong> Aşırı alım/satım seviyeleri piyasa koşullarına göre ayarlanır</span>
                </li>
              </ul>
              
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-4">
                  <p className="text-amber-200 text-sm flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                    RSI tek başına kullanılmamalıdır. Profesyonel sistemler RSI'ı onlarca gösterge ile birlikte değerlendirir.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Section 5 - Common Mistakes */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-red-400" />
                </div>
                RSI Kullanırken Yapılan Hatalar
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-red-950/20 rounded-lg border border-red-500/20">
                  <span className="text-red-400 font-bold">❌</span>
                  <div>
                    <h3 className="text-white font-bold mb-1">RSI 70'i geçti = Hemen sat</h3>
                    <p className="text-slate-400 text-sm">
                      Güçlü yükseliş trendlerinde RSI uzun süre 70 üzerinde kalabilir. Sadece RSI'a bakarak 
                      satış yapmak erken çıkışa neden olabilir.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-red-950/20 rounded-lg border border-red-500/20">
                  <span className="text-red-400 font-bold">❌</span>
                  <div>
                    <h3 className="text-white font-bold mb-1">RSI 30'un altında = Kesin alım</h3>
                    <p className="text-slate-400 text-sm">
                      Düşüş trendlerinde RSI uzun süre 30 altında kalabilir. "Ucuz" görünen varlık daha da 
                      ucuzlayabilir. Her zaman stop-loss kullanın.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 bg-red-950/20 rounded-lg border border-red-500/20">
                  <span className="text-red-400 font-bold">❌</span>
                  <div>
                    <h3 className="text-white font-bold mb-1">Tek zaman diliminde analiz</h3>
                    <p className="text-slate-400 text-sm">
                      Sadece 15 dakikalık grafikte RSI'a bakmak yanıltıcı olabilir. 
                      En az 2-3 farklı zaman diliminde kontrol edin.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sık Sorulan Sorular</h2>
              
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Related Articles */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">İlgili Makaleler</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/rehber/macd-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">MACD Nedir?</h3>
                      <p className="text-slate-400 text-sm">RSI ile birlikte kullanılan güçlü momentum göstergesi</p>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/destek-direnc-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">Destek ve Direnç Nedir?</h3>
                      <p className="text-slate-400 text-sm">RSI sinyallerini doğrulamak için kritik seviyeler</p>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/fibonacci-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">Fibonacci Nedir?</h3>
                      <p className="text-slate-400 text-sm">Geri çekilme ve hedef seviyelerini belirleme</p>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/tp-ve-stop-loss-nedir">
                  <Card className="border-slate-700/50 bg-slate-900/30 hover:border-brand-green/50 transition-colors">
                    <CardContent className="p-4">
                      <h3 className="text-white font-bold mb-1">TP ve Stop Loss Nedir?</h3>
                      <p className="text-slate-400 text-sm">Risk yönetimi ve kar alma stratejileri</p>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            <ShareBox 
              title="RSI Nedir? (Relative Strength Index) Detayli Rehber"
              url="/rehber/rsi-nedir"
              hashtags="#kripto #RSI #teknikanaliz #trading"
            />

          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Tüm Rehberler
            </Link>
            <Link to="/rehber/macd-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Sonraki: MACD Nedir?
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-blue-900/20 to-brand-green/10 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              RSI Dahil 200+ Göstergeyi Analiz Eden AI
            </h3>
            <p className="text-slate-400 mb-6">
              Borsamex'in Sniper Engine'i RSI, MACD ve daha fazlasını birlikte değerlendirerek sinyal üretir.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Dene <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default RSINedirPage;
